import React from 'react';
import { useNavigate } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget';

const LAST_UPDATED = 'June 5, 2026';

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: '#AFA9EC', marginBottom: 12, letterSpacing: '-0.2px' }}>{title}</h2>
      <div style={{ fontSize: 14, color: 'rgba(240,239,233,0.7)', lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

function P({ children }) {
  return <p style={{ margin: '0 0 10px' }}>{children}</p>;
}

function Ul({ items }) {
  return (
    <ul style={{ margin: '8px 0 10px', paddingLeft: 20 }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }}>{item}</li>)}
    </ul>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: '#0e0e0c', color: '#f0efe9', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Nav */}
      <div style={{ borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#7F77DD,#1D9E75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>F</span>
          </div>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.8px', color: '#f0efe9' }}>FLO</span>
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 24px 80px' }}>
        <div style={{ marginBottom: 48 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-1px', marginBottom: 10 }}>Privacy Policy</h1>
          <p style={{ fontSize: 13, color: 'rgba(240,239,233,0.4)' }}>Last updated: {LAST_UPDATED}</p>
        </div>

        <Section title="Overview">
          <P>FLO ("we", "our", or "us") operates an AI-powered insurance sales platform. This Privacy Policy explains how we collect, use, and protect your information when you use FLO and the services of agents who use FLO to communicate with you.</P>
          <P>By using FLO, you agree to the collection and use of information as described in this policy.</P>
        </Section>

        <Section title="Information We Collect">
          <P><strong style={{ color: '#f0efe9' }}>Account information:</strong> When you register, we collect your name, email address, phone number, agency name, and password (stored as a one-way bcrypt hash).</P>
          <P><strong style={{ color: '#f0efe9' }}>Lead and contact data:</strong> Agents upload or create lead records that include names, phone numbers, email addresses, and coverage preferences.</P>
          <P><strong style={{ color: '#f0efe9' }}>Conversation data:</strong> SMS conversations between the AI and leads are stored to provide context for ongoing conversations and for agent review.</P>
          <P><strong style={{ color: '#f0efe9' }}>Billing information:</strong> Payment details are processed by Stripe and are not stored on our servers. We store subscription status and plan information.</P>
          <P><strong style={{ color: '#f0efe9' }}>Usage data:</strong> We collect server logs, feature usage patterns, and error reports to improve the platform.</P>
          <P><strong style={{ color: '#f0efe9' }}>Agent Responsibility:</strong> Insurance agents using FLO are solely responsible for obtaining and maintaining proper consent from their contacts before initiating SMS communications. FLO does not verify agent compliance with consent requirements.</P>
        </Section>

        <Section title="How We Use Your Information">
          <Ul items={[
            'To provide and operate the FLO platform',
            'To send AI-generated SMS messages on behalf of licensed insurance agents',
            'To process subscription payments and manage billing',
            'To send transactional emails (appointment confirmations, password resets)',
            'To generate AI responses using conversation history',
            'To improve platform performance and fix issues',
          ]} />
        </Section>

        <Section title="SMS Messaging">
          <P>FLO uses <strong style={{ color: '#f0efe9' }}>Twilio</strong> to send and receive SMS messages. When you receive a text from a FLO-powered agent, your phone number is stored in our system to route replies correctly.</P>
          <P><strong style={{ color: '#f0efe9' }}>Opt-out:</strong> You can stop receiving SMS messages at any time by replying <strong style={{ color: '#f0efe9' }}>STOP</strong> to any message. Your number will be immediately flagged as opted out and no further automated messages will be sent. You may also reply HELP for assistance.</P>
          <P>Message and data rates may apply. Message frequency varies by agent configuration.</P>
          <P>FLO complies with the Telephone Consumer Protection Act (TCPA) and CTIA messaging guidelines.</P>
        </Section>

        <Section title="Payment Processing">
          <P>All subscription payments are processed by <strong style={{ color: '#f0efe9' }}>Stripe, Inc.</strong> FLO does not store your credit card number, CVV, or full payment details. Stripe's privacy policy governs the handling of your payment data.</P>
          <P>Your subscription plan, status, and billing history are stored in our system to manage your account.</P>
        </Section>

        <Section title="Email Communications">
          <P>FLO uses <strong style={{ color: '#f0efe9' }}>SendGrid (Twilio)</strong> to deliver transactional emails including appointment confirmations, password reset links, and account notifications. Your email address is transmitted to SendGrid solely to deliver these messages.</P>
        </Section>

        <Section title="AI Processing">
          <P>FLO uses the <strong style={{ color: '#f0efe9' }}>Anthropic Claude API</strong> to generate AI responses in SMS conversations. Conversation history, lead context, and agent configuration are sent to Anthropic's API to generate each response.</P>
          <P>Anthropic's use of data submitted via API is governed by Anthropic's privacy policy. Data submitted through the API is not used to train Anthropic's models.</P>
        </Section>

        <Section title="Data Sharing">
          <P>We do not sell your personal information. We share data only with the service providers listed above (Twilio, Stripe, SendGrid, Anthropic) and only as necessary to operate the platform. We may disclose information if required by law or to protect our legal rights.</P>
        </Section>

        <Section title="Data Retention">
          <P>Account data is retained for as long as your account is active. Conversation history and lead data are retained to support ongoing agent workflows. You may request deletion of your account and associated data by contacting us.</P>
          <P>Opted-out phone numbers are retained indefinitely to ensure we do not contact them again.</P>
        </Section>

        <Section title="Security">
          <P>We use industry-standard security practices including encrypted connections (HTTPS/TLS), bcrypt password hashing, and JWT-based authentication. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</P>
        </Section>

        <Section title="Your Rights">
          <Ul items={[
            'Request access to the personal data we hold about you',
            'Request correction of inaccurate data',
            'Request deletion of your account and data',
            'Opt out of SMS communications at any time by replying STOP',
          ]} />
          <P>To exercise these rights, use the contact form below.</P>
        </Section>

        <Section title="Changes to This Policy">
          <P>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date. Continued use of FLO after changes constitutes acceptance of the revised policy.</P>
        </Section>

        <Section title="Contact Us">
          <P>If you have questions about this Privacy Policy or your data, chat with FLO directly below.</P>
          <div style={{ marginTop: 16 }}>
            <ChatWidget />
          </div>
        </Section>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)', padding: '24px 32px', display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Privacy Policy</button>
        <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Terms of Service</button>
        <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'rgba(240,239,233,0.4)', cursor: 'pointer', fontSize: 12 }}>Sign In</button>
      </div>
    </div>
  );
}
